import { CARD_HEIGHT, CARD_WIDTH } from "./CaptainCard.js";

/**
 * Rasterizing an SVG works by serializing it, loading it into an <img>, and
 * drawing that to a canvas. An SVG loaded that way is a sandboxed document: it
 * cannot fetch anything, so nothing the card draws may reference an external
 * URL. The card is now entirely self-contained, which is what keeps the canvas
 * untainted and the PNG export working.
 */

export interface RenderOptions {
  /** Output width in pixels. Height follows the card's aspect ratio. */
  width?: number;
}

/** Rasterize a live <svg> element to a PNG blob. */
export async function svgToPngBlob(svg: SVGSVGElement, options: RenderOptions = {}): Promise<Blob> {
  const width = options.width ?? CARD_WIDTH;
  const height = Math.round((width / CARD_WIDTH) * CARD_HEIGHT);

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));

  const source = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([source], { type: "image/svg+xml;charset=utf-8" }));

  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("The card could not be rasterized."));
      image.src = url;
    });

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("Canvas is unavailable in this browser.");
    context.drawImage(image, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob === null
            ? reject(new Error("The card could not be encoded as PNG."))
            : resolve(blob),
        "image/png",
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
